import { Injectable, Type, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { merge, skip } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { TrainingAssistantService } from '../../services/felixApi/training-assistant.service';
import { TrainingAssistantChatResponse } from '../../dtos/training-assistant-chat';
import { ThomasChartActions, ThomasChartCardComponent, ThomasChartCardState } from './thomas-chart-card.component';
import { navigation } from '../../app-navigation';

const PHONE_SIZE = 620;

export type { TrainingAssistantChatResponse } from '../../dtos/training-assistant-chat';

export interface ThomasCard {
  component: Type<unknown>;
  inputs: Record<string, unknown>;
}

/** The open dashboard page (a lazy chunk the header widget must not import) registers this while it is open. */
export interface ThomasChartHost extends ThomasChartActions {
  buildPage(): { pageId: string, pageContext: string } | null;
}

// This is the host-app seam: identity, host services, page context, prompts and cards.
@Injectable({ providedIn: 'root' })
export class ThomasAppConfig {
  readonly contextChanged;

  readonly global = {
    returnError: (err: unknown): string => {
      if (err instanceof HttpErrorResponse) {
        return err.error?.message ?? err.error?.detail ?? err.message;
      }
      return err instanceof Error ? err.message : String(err ?? 'Unknown error');
    }
  };

  readonly auth = inject(AuthService);
  readonly notifications = inject(NotificationService);
  readonly transport = inject(TrainingAssistantService);

  private chartHost: ThomasChartHost | null = null;
  // read at click time, so a card from earlier in the chat works once its dashboard is open again
  private readonly chartActions: ThomasChartActions = {
    blockReason: (proposal, companyId) => this.chartHost
      ? this.chartHost.blockReason(proposal, companyId)
      : `Open the ${proposal.dashboardName} dashboard to use this.`,
    open: (proposal, companyId) => this.chartHost
      ? this.chartHost.open(proposal, companyId)
      : `Open the ${proposal.dashboardName} dashboard to use this.`
  };

  constructor() {
    const auth = this.auth;
    this.contextChanged = merge(auth.currentUser.pipe(skip(1)), auth.currentCompany.pipe(skip(1)));
  }

  // this app in the API's list of Thomas apps; a new saved prompt shows here by default
  readonly appId = 'Hub';

  // this app plus the ones the 9-dot launcher shows, by the API's ThomasApp names
  openableApps(): string[] {
    const launcher = navigation.find(item => item.text === 'Applications')?.items ?? [];
    return [this.appId, ...launcher.filter(app => app.visible !== false).map(app => app.text === 'Admin' ? 'AdminPortal' : app.text)];
  }

  readonly dockable = true;
  readonly dockMinPageWidth = 1000;

  get available(): boolean {
    const user = this.auth.getCurrentUser();
    return !!user?.id && !!(user.isSuperUser || user.canUseAi) && this.auth.getCurrentCompany() !== undefined;
  }

  get phoneSize(): boolean {
    return window.innerWidth <= PHONE_SIZE;
  }

  get contextKey(): string {
    return `${this.auth.getCurrentUser()?.id ?? 0}:${this.auth.getCurrentCompany()?.id ?? 0}`;
  }

  registerChartHost(host: ThomasChartHost) {
    this.chartHost = host;
  }

  unregisterChartHost(host: ThomasChartHost) {
    if (this.chartHost === host) {
      this.chartHost = null;
    }
  }

  buildPage(): { pageId: string, pageContext: string } | null {
    return this.chartHost?.buildPage() ?? null;
  }

  createCards(response: TrainingAssistantChatResponse): ThomasCard[] {
    if (!response.chartProposal) {
      return [];
    }
    const state: ThomasChartCardState = { companyId: this.auth.getCurrentCompany()?.id, error: null };
    return [{ component: ThomasChartCardComponent, inputs: { proposal: response.chartProposal, actions: this.chartActions, state } }];
  }
}
